
import { calculateVariability } from './helpers';
import { generateCashFlow } from '../financial';
import type { CashFlowParams, CashFlowPrediction } from './types';

/**
 * AI-powered cash flow prediction with risk assessment
 */
export function aiCashFlowPrediction(
  historicalData: any[],
  assumptions: any,
  forecastPeriods: number = 12
): CashFlowPrediction {
  const cashFlowHistory = historicalData.map(d => d.cashFlow || d.netIncome || 0);
  
  // Generate base cash flow forecast
  const params: CashFlowParams = {
    initialInvestment: 0,
    annualRevenue: assumptions.profitLoss?.revenueGrowthRate || 100000,
    annualCosts: assumptions.profitLoss?.costOfGoodsSoldPercentage || 65000,
    growthRate: (assumptions.profitLoss?.revenueGrowthRate || 10) / 100,
    discountRate: (assumptions.general?.discountRate || 8) / 100,
    years: Math.ceil(forecastPeriods / 12)
  };
  
  const projections = generateCashFlow(params);
  const cashFlowForecast = projections.slice(1).flatMap(p => 
    Array(12).fill(p.cashFlow / 12)
  ).slice(0, forecastPeriods);
  
  // Risk assessment based on volatility and trends
  const volatility = calculateVariability(cashFlowHistory);
  const negativePeriodsRatio = cashFlowHistory.filter(cf => cf < 0).length / cashFlowHistory.length;
  
  let riskLevel: 'low' | 'medium' | 'high' = 'low';
  let riskFactors: string[] = [];
  let riskProbability = 0.1;
  
  if (volatility > 0.3 || negativePeriodsRatio > 0.3) {
    riskLevel = 'high';
    riskProbability = 0.7;
    riskFactors.push('High cash flow volatility', 'Frequent negative periods');
  } else if (volatility > 0.15 || negativePeriodsRatio > 0.1) {
    riskLevel = 'medium';
    riskProbability = 0.4;
    riskFactors.push('Moderate cash flow variability');
  }
  
  // Generate recommendations
  const recommendedActions: string[] = [];
  if (riskLevel === 'high') {
    recommendedActions.push('Establish larger cash reserves', 'Consider diversifying revenue streams');
  }
  if (volatility > 0.2) {
    recommendedActions.push('Implement better cash flow forecasting', 'Review payment terms with customers');
  }
  
  return {
    cashFlowForecast,
    riskAssessment: {
      level: riskLevel,
      factors: riskFactors,
      probability: riskProbability
    },
    recommendedActions
  };
}
